'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import MatrixPlanTabs from '../../plans/MatrixPlanTabs';
import MatrixPlanLinks from '../../plans/MatrixPlanLinks';
import MatrixPlanSelector from '../../plans/MatrixPlanSelector';
import { ProductProps } from '../../../types/products';
import EditablePropsType, { ComposerBlock } from '../../../types/composer';
import { Plan, PlansPage } from '../../../types/plans';
import axios from 'axios';
import { fetchPlans } from '../../../lib/shared/plans';
import { trackPlansView } from '../../plans/utils';
import {
  features,
  getProductPlan,
} from '../../../lib/composer/blocks/matrixHelpers';

/**
 * Returns the price type based on the dailyPrice and monthlyPrice flags.
 * @param {boolean} dailyPrice - Whether to show the daily price.
 * @param {boolean} monthlyPrice - Whether to show the monthly price.
 * @returns {'daily' | 'monthly' | null} The price type to display.
 */
export const getMonthlyDailyPrice = (
  dailyPrice: boolean,
  monthlyPrice: boolean,
) => {
  if (dailyPrice) {
    return 'daily';
  }
  if (monthlyPrice) {
    return 'monthly';
  }
  return null;
};

export type MatrixProduct = ProductProps & {
  name: string;
  originName?: string;
  color: string;
  displayDescription?: string;
  planVersion?: string;
};

export type MatrixFeatureList = {
  feature: string;
  tooltipText?: string;
  enabledColumns: string[];
};

type Props = {
  products: MatrixProduct[];
  plans: Plan[];
  plansPage: PlansPage;
  planLengths?: (string | number)[];
  featureList?: MatrixFeatureList[];
  planLengthSelector?: string;
  monthlyPrice: boolean;
  planVersion?: string;
  footerText?: string;
  dailyPrice: boolean;
};

/**
 * Return Matrix Plan Selector Page component.
 * This is for use in admin/composer rather than the main site.
 * FeatureList: an object per matrix row with enabledColumns prop of two strings rather than codes - change to codes.
 */
const MatrixColumns: any = ({
  products: blockProducts,
  plans: _plans = [],
  plansPage,
  planLengths: _planLengths,
  featureList = [],
  planLengthSelector = 'tabs',
  monthlyPrice,
  planVersion = '',
  footerText = '',
  dailyPrice,
}: Props) => {
  const { deal } = plansPage || {};
  const monthlyDailyPrice = getMonthlyDailyPrice(dailyPrice, monthlyPrice);

  const [products, setProducts] = useState<MatrixProduct[]>(blockProducts);
  const [plans, setPlans] = useState<Plan[]>(_plans);

  const planLengths = _planLengths?.filter(Boolean)?.length
    ? _planLengths
        .filter(Boolean)
        .map((planLength) => parseInt(planLength as string, 10))
    : [6, 12, 24];

  const initialActivePlanLength =
    planLengths[Math.floor(planLengths.length / 2)];
  const [activePlanLength, setActivePlanLength] = useState(
    initialActivePlanLength,
  );

  useEffect(() => {
    setActivePlanLength(initialActivePlanLength);
  }, [initialActivePlanLength]);

  useEffect(() => {
    const trackPayload = plans?.filter(
      (plan) => plan.intervalLength === activePlanLength,
    );
    trackPlansView(trackPayload);
  }, [plans, activePlanLength]);

  useEffect(() => {
    const getData = async () => {
      const products = await MatrixColumns.getProducts(blockProducts);
      setProducts(products);
      const plans = await fetchPlans({
        productIDs: blockProducts.map(({ productID }) => productID),
        planLengths,
        planVersion: planVersion || '', // this needs to be set by the composer UI
      });

      setPlans(plans);
    };

    if (
      !blockProducts.map(({ productCode }) => productCode).filter(Boolean)
        .length
    )
      getData();
  }, [blockProducts, _planLengths, planVersion]);

  const productsFeatures = useMemo(() => {
    const filteredFeatureList = featureList.filter(({ feature }) =>
      Boolean(feature),
    );

    const _products = products.filter(({ productID }) =>
      getProductPlan(productID, plans, activePlanLength),
    );

    // for a product to have a checked feature, the corresponding feature must be enabled and the product name must be non-empty
    const defaultProductFeatures = features
      .map((feature) => {
        return {
          title: feature.title,
          products: _products.map((product) => ({
            ...product,
            enabled: feature.isEnabled(product) && product.name.length > 0,
          })),
        };
      })
      .filter((feature) =>
        feature.products.map(({ enabled }) => enabled).includes(true),
      );

    const customProductsFeatures = filteredFeatureList
      .map(({ feature, tooltipText, enabledColumns = [] }) => {
        return {
          title: feature,
          tooltipText,
          products: _products.map((product) => ({
            ...product,
            enabled: enabledColumns.some((element) => {
              const enabledList: { [key: string]: string } = {
                'Basic Sciences': 'BasSci',
                'Clinical Sciences': 'CliSci',
                'Osmosis Suite': 'Suite',
              };
              return (
                product.productCode.includes(
                  enabledList[element as keyof typeof enabledList],
                ) && product.name.length > 0
              );
            }),
          })),
        };
      })
      .filter((feature) =>
        feature.products.map(({ enabled }) => enabled).includes(true),
      );

    const isFeatureListEnabled = Boolean(filteredFeatureList.length);

    return isFeatureListEnabled
      ? customProductsFeatures
      : defaultProductFeatures;
  }, [featureList, products, plans, activePlanLength]);

  const linkProps = {
    planLengths,
    activePlanLength,
    setActivePlanLength,
  };

  return (
    <Box className="w-full">
      <Box
        id="outer-wrapper"
        className="flex flex-col items-center w-full pt-4 border-t border-t-gray-200 bg-white"
      >
        {planLengths.length > 0 && (
          <Box className="mx-auto mb-4 w-full max-w-[1000px]">
            {planLengthSelector === 'Links' ? (
              <MatrixPlanLinks {...linkProps} />
            ) : (
              <MatrixPlanTabs
                planLengths={planLengths}
                activePlanLength={activePlanLength}
                setActivePlanLength={setActivePlanLength}
              />
            )}
          </Box>
        )}
        <Box className="w-full mb-5">
          <MatrixPlanSelector
            monthlyDailyPrice={monthlyDailyPrice}
            products={products}
            plans={plans}
            activePlanLength={activePlanLength}
            deal={deal}
            productFeatures={productsFeatures}
            footerText={footerText}
          />
        </Box>
      </Box>
    </Box>
  );
};

MatrixColumns.schema = {
  name: 'matrix-plan-selector-page',
  label: 'Matrix Plan Selector',
  editableProps: [
    {
      label: 'Products',
      name: 'products',
      type: EditablePropsType.Array,
      items: {
        label: 'Product',
        name: 'product',
        type: EditablePropsType.Object,
        properties: {
          name: {
            label: 'Name',
            name: 'name',
            type: EditablePropsType.Text,
          },
          displayDescription: {
            label: 'Description',
            name: 'displayDescription',
            type: EditablePropsType.Text,
          },
          color: {
            label: 'Color',
            name: 'color',
            type: EditablePropsType.Select,
            selectOptions: {
              options: [
                { label: 'Green', value: '#376D37' },
                { label: 'Yellow', value: '#83690C' },
                { label: 'Blue', value: '#1A61B2' },
              ],
            },
          },
          productID: {
            label: 'Product Code',
            name: 'productID',
            type: EditablePropsType.Product,
          },
        },
      },
    },
    {
      label: 'Plan Version',
      name: 'planVersion',
      type: EditablePropsType.Text,
    },
    {
      label: 'Plan Lengths',
      name: 'planLengths',
      description: 'Plan lengths to display in months. Defaults to 6, 12, 24.',
      type: EditablePropsType.Array,
      items: {
        type: EditablePropsType.Text,
        name: 'planLength',
        label: 'Plan Length',
        required: true,
      },
    },
    {
      label: 'Feature list',
      name: 'featureList',
      type: EditablePropsType.Array,
      items: {
        type: EditablePropsType.Object,
        properties: {
          feature: {
            type: EditablePropsType.Text,
            label: 'Feature',
            name: 'feature',
          },
          tooltipText: {
            type: EditablePropsType.Text,
            label: 'Tooltip text',
            name: 'tooltipText',
          },
          enabledColumns: {
            description:
              'Please enter: Basic Sciences, Clinical Sciences, or Osmosis Suite',
            type: EditablePropsType.Array,
            name: 'enabledColumns',
            label: 'Enabled Columns',
            items: {
              type: EditablePropsType.Text,
            },
          },
        },
      },
    },
    {
      label: 'Plan Length Selector',
      name: 'planLengthSelector',
      description:
        'Select the type of plan length selector to use. Defaults to Tabs.',
      type: EditablePropsType.Select,
      selectOptions: {
        options: [
          { label: '', value: '' },
          { label: 'Tabs', value: 'Tabs' },
          { label: 'Links', value: 'Links' },
        ],
      },
    },
    {
      label: 'Add monthly billing',
      name: 'monthlyPrice',
      description: 'Show monthly billing price',
      type: EditablePropsType.Boolean,
    },
    {
      label: 'Add daily billing',
      name: 'dailyPrice',
      description: '(deselect monthly)',
      type: EditablePropsType.Boolean,
    },
    {
      label: 'Add text beneath the matrix',
      name: 'footerText',
      description: 'Text to display beneath the matrix',
      type: EditablePropsType.Text,
    },
  ],
};

/**
 * Returns the program and planLengths arguments from a ComposerBlock.
 * @param {ComposerBlock} block - The ComposerBlock to extract arguments from.
 * @returns {Object} An object containing the program and planLengths arguments.
 */
MatrixColumns.getPlansParams = (block: ComposerBlock) => {
  const { products } = block.args;
  const productIDs = products.map(
    (product: { productID: number }) => product.productID,
  );

  return { productIDs };
};

MatrixColumns.getProducts = async function getProducts(
  blockProducts: MatrixProduct[],
  hostname = '',
) {
  const response = await axios.get<null, { data: { data: ProductProps[] } }>(
    hostname + '/v0/products',
  );
  const allProducts = response?.data?.data;
  const products = blockProducts.map((blockProduct) => {
    const productData = allProducts.find(
      ({ productID }) => blockProduct.productID === productID,
    );

    return {
      ...productData,
      ...blockProduct,
    };
  });

  return products;
};

export default MatrixColumns;
